<template>
  <DefaultCrudView
    @create-new="newPipeStep"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">
      <v-data-table
        :headers="headers"
        :items="pipesteps"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.pipeline_id="{ item }">
          {{ getPipelineTitle(item.pipeline_id) }}
        </template>

        <template v-slot:item.routine_id="{ item }">
          {{ getRoutineTitle(item.routine_id) }}
        </template>

        <template v-slot:item.fixed_param="{ item }">
          <v-tooltip bottom v-if="item.fixed_param && item.fixed_param.length > 50">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" style="max-width: 200px; display: inline-block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                {{ item.fixed_param }}
              </span>
            </template>
            <span>{{ item.fixed_param }}</span>
          </v-tooltip>
          <span v-else>{{ item.fixed_param || '' }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editPipeStep(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click.stop="deletePipeStep(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <DefaultModal 
        :title="getModalTitle()" 
        ref="modal" 
        v-on:submit="save"
      >
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">{{ $t('confirm') }}</v-card-title>
          <v-card-text>
            {{ $t('confirm_delete') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="confirmDialog = false">
              {{ $t('close') }}
            </v-btn>
            <v-btn color="red darken-1" text @click="confirmDelete">
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </DefaultCrudView>
</template>

<script>
import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import pipestepApi from '@/services/api/pipeline/pipestep.js'
import pipelineApi from '@/services/api/pipeline/pipeline.js'
import routineApi from '@/services/api/pipeline/routine.js'

export default {
  name: 'PipeStep',
  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent
  },
  data() {
    return {
      loading: false,
      pipesteps: [],
      options: {
        sortBy: ['pipe_order'],
        sortDesc: [false],
      },
      itemsLength: 0,
      selectedPipeStep: null,
      pipelines: [],
      routines: [],
      confirmDialog: false,
      pipeStepToDelete: null,
      crudActions: [
        {
          text: "create_pipestep",
          eventToEmit: "create-new",
        }
      ],
      headers: [
        { text: 'ID', value: 'pipe_step_id', sortable: true },
        { text: 'Pipeline', value: 'pipeline_id', sortable: true },
        { text: 'Routine', value: 'routine_id', sortable: true },
        { text: 'Order', value: 'pipe_order', sortable: true },
        { text: 'Fixed Param', value: 'fixed_param', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      fields: [
        {
          name: 'pipeline_id',
          type: 'select',
          label: 'Pipeline',
          required: true,
          items: [],
          itemText: 'text',
          itemValue: 'value',
          value: null,
          disabled: false
        },
        {
          name: 'routine_id',
          type: 'select',
          label: 'Routine',
          required: true,
          items: [],
          itemText: 'text',
          itemValue: 'value',
          value: null,
          disabled: false
        },
        {
          name: 'pipe_order',
          type: 'number',
          label: 'Order',
          required: true,
          value: null
        },
        {
          name: 'fixed_param',
          type: 'textarea',
          label: 'Fixed Parameters',
          required: false,
          value: null
        }
      ]
    }
  },
  computed: {
    breadcrumbs() {
      const items = [
        {
          text: 'PipeSteps',
          prependIcon: 'mdi-pipe',
        }
      ]
      
      // Add context based on query parameters
      if (this.$route.query.pipeline_id) {
        const pipeline = this.pipelines.find(p => p.pipeline_id == this.$route.query.pipeline_id)
        if (pipeline) {
          items.push({
            text: `Pipeline: ${pipeline.title || pipeline.pipeline_id}`,
          })
        }
      }
      
      if (this.$route.query.routine_id) {
        const routine = this.routines.find(r => r.routine_id == this.$route.query.routine_id)
        if (routine) {
          items.push({
            text: `Routine: ${routine.title || routine.routine_id}`,
          })
        }
      }
      
      return items
    }
  },
  mounted() {
    this.loadPipelines()
    this.loadRoutines()
    this.loadPipesteps()
  },
  watch: {
    '$route.query.pipeline_id': function() {
      this.loadPipesteps()
    },
    '$route.query.routine_id': function() {
      this.loadPipesteps()
    },
    options: {
      handler () {
        this.loadPipesteps()
      },
      deep: true,
    }
  },
  methods: {
    newPipeStep() {
      this.selectedPipeStep = null
      this.resetForm()

      // Enable pipeline_id and routine_id fields for creation
      const pipelineField = this.fields.find(f => f.name === 'pipeline_id')
      const routineField = this.fields.find(f => f.name === 'routine_id')
      
      if (pipelineField) {
        pipelineField.disabled = false
      }
      if (routineField) {
        routineField.disabled = false
      }

      // Pre-fill pipeline_id or routine_id if available in query
      if (this.$route.query.pipeline_id) {
        if (pipelineField) {
          pipelineField.value = parseInt(this.$route.query.pipeline_id)
        }
      }
      
      if (this.$route.query.routine_id) {
        if (routineField) {
          routineField.value = parseInt(this.$route.query.routine_id)
        }
      }
      
      this.$refs.modal.openModal()
    },

    editPipeStep(pipeStep) {
      this.selectedPipeStep = pipeStep
      this.populateForm(pipeStep)
      
      // Disable pipeline_id and routine_id fields for editing (they are fixed)
      const pipelineField = this.fields.find(f => f.name === 'pipeline_id')
      const routineField = this.fields.find(f => f.name === 'routine_id')
      
      if (pipelineField) {
        pipelineField.disabled = true
      }
      if (routineField) {
        routineField.disabled = true
      }
      
      this.$refs.modal.openModal()
    },

    deletePipeStep(pipeStep) {
      this.pipeStepToDelete = pipeStep
      this.confirmDialog = true
    },

    confirmDelete() {
      if (this.pipeStepToDelete) {
        pipestepApi.deletePipestep(this.pipeStepToDelete.pipe_step_id)
          .then(() => {
            this.$store.dispatch('app/openAppSuccessMessage', this.$t('PipeStep deleted'))
            this.loadPipesteps()
            this.confirmDialog = false
            this.pipeStepToDelete = null
          })
          .catch((error) => {
            console.error('Error deleting pipestep:', error)
            this.$store.dispatch('app/openAppSuccessMessage', this.$t('Error deleting pipestep'))
            this.confirmDialog = false
            this.pipeStepToDelete = null
          })
      }
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = this.getFormData()
      
      if (this.selectedPipeStep) {
        // Update existing pipestep
        pipestepApi.updatePipestep(this.selectedPipeStep.pipe_step_id, {
          pipe_order: formData.pipe_order,
          fixed_param: formData.fixed_param
        })
          .then(() => {
            this.$store.dispatch('app/openAppSuccessMessage', this.$t('PipeStep updated'))
            this.$refs.modal.closeModal()
            this.loadPipesteps()
          })
          .catch((error) => {
            console.error('Error updating pipestep:', error)
            this.$refs.modal.submitting = false
          })
      } else {
        // Create new pipestep
        pipestepApi.createPipestep(formData)
          .then(() => {
            this.$store.dispatch('app/openAppSuccessMessage', this.$t('PipeStep created'))
            this.$refs.modal.closeModal()
            this.loadPipesteps()
          })
          .catch((error) => {
            console.error('Error creating pipestep:', error)
            this.$refs.modal.submitting = false
          })
      }
    },

    loadPipesteps() {
      const pipeline_id = this.$route.query.pipeline_id
      const routine_id = this.$route.query.routine_id
      
      this.loading = true
      
      if (pipeline_id) {
        pipestepApi.getPipestepFromPipeline(pipeline_id)
          .then(response => {
            // Try different possible response structures
            let pipesteps = []
            if (response.data) {
              if (Array.isArray(response.data)) {
                pipesteps = response.data
              } else if (response.data.items && Array.isArray(response.data.items)) {
                pipesteps = response.data.items
              } else if (response.data.data && Array.isArray(response.data.data)) {
                pipesteps = response.data.data
              } else if (response.data.pipesteps && Array.isArray(response.data.pipesteps)) {
                pipesteps = response.data.pipesteps
              }
            }
            
            this.pipesteps = pipesteps
            this.itemsLength = pipesteps.length
            this.loading = false
          })
          .catch(error => {
            console.error('Error loading pipesteps from pipeline:', error)
            this.pipesteps = []
            this.itemsLength = 0
            this.loading = false
          })
      } else if (routine_id) {
        pipestepApi.getPipestepFromRoutine(routine_id)
          .then(response => {
            // Try different possible response structures
            let pipesteps = []
            if (response.data) {
              if (Array.isArray(response.data)) {
                pipesteps = response.data
              } else if (response.data.items && Array.isArray(response.data.items)) {
                pipesteps = response.data.items
              } else if (response.data.data && Array.isArray(response.data.data)) {
                pipesteps = response.data.data
              } else if (response.data.pipesteps && Array.isArray(response.data.pipesteps)) {
                pipesteps = response.data.pipesteps
              }
            }
            
            this.pipesteps = pipesteps
            this.itemsLength = pipesteps.length
            this.loading = false
          })
          .catch(error => {
            console.error('Error loading pipesteps from routine:', error)
            this.pipesteps = []
            this.itemsLength = 0
            this.loading = false
          })
      } else {
        this.pipesteps = []
        this.itemsLength = 0
        this.loading = false
      }
    },

    loadPipelines() {
      pipelineApi.getAllPipelines()
        .then(response => {
          this.pipelines = response.data.items || response.data || []
          const pipelineField = this.fields.find(f => f.name === 'pipeline_id')
          if (pipelineField) {
            pipelineField.items = this.pipelines.map(p => ({
              value: p.pipeline_id,
              text: `${p.pipeline_id} - ${p.pipeline_key || 'Unknown'}`
            }))
          }
        })
        .catch(error => {
          console.error('Error loading pipelines:', error)
          this.pipelines = []
        })
    },

    loadRoutines() {
      routineApi.getAllRoutines()
        .then(response => {
          this.routines = response.data.items || response.data || []
          const routineField = this.fields.find(f => f.name === 'routine_id')
          if (routineField) {
            routineField.items = this.routines.map(r => ({
              value: r.routine_id,
              text: `${r.routine_id} - ${r.routine_key || 'Unknown'}`
            }))
          }
        })
        .catch(error => {
          console.error('Error loading routines:', error)
          this.routines = []
        })
    },

    getPipelineTitle(pipelineId) {
      if (!pipelineId) return ''
      
      // Check if we already have it in our cached pipelines
      let pipeline = this.pipelines.find(p => p.pipeline_id == pipelineId)
      if (pipeline) {
        return `${pipelineId} - ${pipeline.pipeline_key || pipeline.name || pipeline.title || 'Unknown'}`
      }
      
      // If not found in cache, fetch individual pipeline asynchronously
      this.fetchPipelineIfNeeded(pipelineId)
      
      return `Pipeline ${pipelineId}`
    },

    getRoutineTitle(routineId) {
      if (!routineId) return ''
      
      // Check if we already have it in our cached routines
      let routine = this.routines.find(r => r.routine_id == routineId)
      if (routine) {
        return `${routineId} - ${routine.routine_key || routine.name || routine.title || 'Unknown'}`
      }
      
      // If not found in cache, fetch individual routine asynchronously
      this.fetchRoutineIfNeeded(routineId)
      
      return `Routine ${routineId}`
    },

    async fetchPipelineIfNeeded(pipelineId) {
      // Check if we're already fetching this pipeline
      if (this.fetchingPipelines && this.fetchingPipelines.has(pipelineId)) {
        return
      }
      
      if (!this.fetchingPipelines) {
        this.fetchingPipelines = new Set()
      }
      this.fetchingPipelines.add(pipelineId)
      
      try {
        const response = await pipelineApi.getPipeline(pipelineId)
        const pipeline = response.data
        if (pipeline) {
          // Add to cache for future use
          const existingIndex = this.pipelines.findIndex(p => p.pipeline_id == pipelineId)
          if (existingIndex === -1) {
            this.pipelines.push(pipeline)
          } else {
            this.pipelines[existingIndex] = pipeline
          }
          // Force reactivity update
          this.$forceUpdate()
        }
      } catch (error) {
        console.error('Error fetching pipeline:', error)
      } finally {
        this.fetchingPipelines.delete(pipelineId)
      }
    },

    async fetchRoutineIfNeeded(routineId) {
      // Check if we're already fetching this routine
      if (this.fetchingRoutines && this.fetchingRoutines.has(routineId)) {
        return
      }
      
      if (!this.fetchingRoutines) {
        this.fetchingRoutines = new Set()
      }
      this.fetchingRoutines.add(routineId)
      
      try {
        const response = await routineApi.getRoutine(routineId)
        const routine = response.data
        if (routine) {
          // Add to cache for future use
          const existingIndex = this.routines.findIndex(r => r.routine_id == routineId)
          if (existingIndex === -1) {
            this.routines.push(routine)
          } else {
            this.routines[existingIndex] = routine
          }
          // Force reactivity update
          this.$forceUpdate()
        }
      } catch (error) {
        console.error('Error fetching routine:', error)
      } finally {
        this.fetchingRoutines.delete(routineId)
      }
    },

    getModalTitle() {
      return this.selectedPipeStep ? this.$t('edit_pipestep') : this.$t('new_pipestep')
    },

    resetForm() {
      this.fields.forEach(field => {
        field.value = null
        // Reset disabled state for pipeline_id and routine_id fields
        if (field.name === 'pipeline_id' || field.name === 'routine_id') {
          field.disabled = false
        }
      })
    },

    populateForm(pipeStep) {
      this.fields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(pipeStep, field.name)) {
          field.value = pipeStep[field.name]
        }
      })
    },

    getFormData() {
      const data = {}
      this.fields.forEach(field => {
        data[field.name] = field.value
      })
      return data
    }
  }
}
</script>

<style scoped>
</style>
